import { isAdmin } from '../services/adminAuth.js';
import { parseLocalDate } from '../services/dates.js';

export const JobScheduler = {
  data() {
    return {
      view: "week",
      selectedDate: new Date(),
      selectedJob: null,
      jobs: {},
      loading: true,
      showScheduleModal: false,
      showJobDetailsModal: false,
      currentUserProfile: null,
      isAdmin: false,
      scheduleForm: {
        date: new Date().toISOString().split("T")[0],
        timeSlot: "",
        notes: "",
        status: "scheduled",
        duration: 60
      },
      statuses: [
        { value: "scheduled", label: "Scheduled", class: "alert-primary" },
        { value: "completed", label: "Completed", class: "alert-success" },
        { value: "cancelled", label: "Cancelled", class: "alert-danger" },
        { value: "unscheduled", label: "Unscheduled", class: "alert-warning" }
      ],
      timeSlotOptions: [],
      dayStartHour: 8,
      dayEndHour: 18,
      modalBackdropVisible: false,
      activeTab: "calendar"
    };
  },

  computed: {
    weekDates() {
      // Create a new date object and set to midnight
      const curr = new Date(this.selectedDate);
      curr.setHours(0, 0, 0, 0);

      // Get current date and day of week
      const currentDay = curr.getDay(); // 0-6, 0 is Sunday
      const currentDate = curr.getDate();

      // Calculate first day of week (Sunday)
      const firstDay = new Date(curr);
      firstDay.setDate(currentDate - currentDay);

      // Generate array of dates for the week
      const dates = [];
      for (let i = 0; i < 7; i++) {
        const date = new Date(firstDay);
        date.setDate(firstDay.getDate() + i);
        dates.push(date);
      }

      return dates;
    },

    filteredJobs() {
      if (!this.jobs) return {};

      // If viewing own profile or not an admin, only show own jobs
      if (!this.isAdmin || !this.currentUserProfile) {
        return Object.fromEntries(
          Object.entries(this.jobs).filter(
            ([_, job]) => job.userId === firebase.auth().currentUser.uid
          )
        );
      }

      // If admin viewing another user's profile, show that user's jobs
      return Object.fromEntries(
        Object.entries(this.jobs).filter(
          ([_, job]) => job.userId === this.currentUserProfile
        )
      );
    },

    calendarDays() {
      return this.weekDates.map((date) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const compareDate = new Date(date);
        compareDate.setHours(0, 0, 0, 0);

        return {
          date: compareDate,
          label: this.formatDateForDisplay(compareDate),
          isToday: compareDate.getTime() === today.getTime(),
          jobs: this.getJobsForDate(compareDate)
        };
      });
    },

    unscheduledJobs() {
      return Object.entries(this.filteredJobs)
        .filter(([_, job]) => !job.scheduledDate && !job.scheduledTime)
        .map(([id, job]) => ({
          id,
          ...job
        }));
    },

    baseTimeSlots() {
      const slots = [];
      for (let hour = this.dayStartHour; hour <= this.dayEndHour; hour++) {
        // Only generate hourly slots for the grid
        slots.push({
          hour,
          minute: 0,
          label: new Date(2000, 0, 1, hour, 0).toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit"
          }),
          time: `${hour.toString().padStart(2, "0")}:00`
        });
      }
      return slots;
    },

    // Separate computed property for scheduling time slots
    availableSchedulingSlots() {
      const slots = [];
      for (let hour = this.dayStartHour; hour <= this.dayEndHour; hour++) {
        for (let minute = 0; minute < 60; minute += 30) {
          slots.push({
            hour,
            minute,
            label: new Date(2000, 0, 1, hour, minute).toLocaleTimeString(
              "en-US",
              {
                hour: "numeric",
                minute: "2-digit"
              }
            ),
            time: `${hour
              .toString()
              .padStart(2, "0")}:${minute.toString().padStart(2, "0")}`
          });
        }
      }
      return slots;
    },

    availableTimeSlots() {
      if (!this.scheduleForm.date) return this.availableSchedulingSlots;

      return this.availableSchedulingSlots.filter((slot) =>
        this.checkSlotAvailability(
          this.scheduleForm.date,
          slot.time,
          this.scheduleForm.duration,
          this.selectedJob?.id
        )
      );
    },

    // Properties for enhanced calendar navigation

    currentMonthWeeks() {
      const weeks = [];
      const currentDate = new Date(this.selectedDate);
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();

      // Get first day of the month
      const firstDay = new Date(year, month, 1);

      // Get last day of the month
      const lastDay = new Date(year, month + 1, 0);

      // Find the first Sunday of the first week that contains days of this month
      const firstWeekStart = new Date(firstDay);
      firstWeekStart.setDate(firstDay.getDate() - firstDay.getDay());

      let weekNumber = 1;
      let currentWeekStart = new Date(firstWeekStart);

      while (currentWeekStart <= lastDay) {
        const weekEnd = new Date(currentWeekStart);
        weekEnd.setDate(currentWeekStart.getDate() + 6);

        // Only include weeks that have at least one day in the current month
        const weekHasMonthDays =
          (currentWeekStart.getMonth() === month &&
            currentWeekStart.getFullYear() === year) ||
          (weekEnd.getMonth() === month && weekEnd.getFullYear() === year) ||
          (currentWeekStart < firstDay && weekEnd > lastDay);

        if (weekHasMonthDays) {
          weeks.push({
            weekNumber,
            startDate: new Date(currentWeekStart),
            endDate: new Date(weekEnd)
          });
          weekNumber++;
        }

        // Move to next week
        currentWeekStart.setDate(currentWeekStart.getDate() + 7);
      }

      return weeks;
    },

    availableYears() {
      const currentYear = new Date().getFullYear();
      const years = [];

      // Generate years from 2 years ago to 2 years in the future
      for (let year = currentYear - 2; year <= currentYear + 2; year++) {
        years.push(year);
      }

      return years;
    },

    months() {
      return [
        { name: "January", short: "Jan" },
        { name: "February", short: "Feb" },
        { name: "March", short: "Mar" },
        { name: "April", short: "Apr" },
        { name: "May", short: "May" },
        { name: "June", short: "Jun" },
        { name: "July", short: "Jul" },
        { name: "August", short: "Aug" },
        { name: "September", short: "Sep" },
        { name: "October", short: "Oct" },
        { name: "November", short: "Nov" },
        { name: "December", short: "Dec" }
      ];
    },

    tempYear: {
      get() {
        return this.selectedDate.getFullYear();
      },
      set(value) {
        const newDate = new Date(this.selectedDate);
        newDate.setFullYear(value);
        this.selectedDate = newDate;
      }
    }
  },

  methods: {
    async checkAdminStatus() {
      const user = firebase.auth().currentUser;
      if (!user) return false;

      try {
        this.isAdmin = await isAdmin();
      } catch (error) {
        console.error("Error checking admin status:", error);
        this.isAdmin = false;
      }
    },

    async fetchJobs() {
      try {
        if (!firebase.auth().currentUser) return;

        // If admin viewing a profile, fetch all jobs and filter in computed property
        if (this.isAdmin) {
          const snapshot = await firebase.database().ref("jobs").once("value");
          this.jobs = snapshot.val() || {};
          return;
        }

        // For regular users, only fetch their jobs
        const snapshot = await firebase
          .database()
          .ref("jobs")
          .orderByChild("userId")
          .equalTo(firebase.auth().currentUser.uid)
          .once("value");

        this.jobs = snapshot.val() || {};
      } catch (error) {
        console.error("Error fetching jobs:", error);
      } finally {
        this.loading = false;
      }
    },

    formatDate(date) {
      if (!date) return "";

      const dateObj = parseLocalDate(date);
      if (!dateObj) {
        console.error("Invalid date provided to formatDate:", date);
        return "";
      }

      return dateObj.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric"
      });
    },

    formatTime(timeSlot) {
      if (!timeSlot) return "";
      const [hours, minutes] = timeSlot.split(":");
      return new Date(2000, 0, 1, hours, minutes).toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "numeric"
      });
    },

    previousWeek() {
      const newDate = new Date(this.selectedDate);
      newDate.setDate(newDate.getDate() - 7);
      this.selectedDate = newDate;
    },

    nextWeek() {
      const newDate = new Date(this.selectedDate);
      newDate.setDate(newDate.getDate() + 7);
      this.selectedDate = newDate;
    },

    getEventPosition(job) {
      if (!job.scheduledTime) return { top: "0px", height: "60px" };

      // Parse time (assuming 24-hour format)
      const [hours, minutes] = job.scheduledTime.split(":").map(Number);

      // Calculate position based on hour increments
      const startMinutes = this.dayStartHour * 60;
      const jobMinutes = hours * 60 + minutes;
      const top = jobMinutes - startMinutes;
      const height = job.duration || 60;

      // Each hour is 60px in height
      return {
        top: `${top}px`,
        height: `${height}px`
      };
    },

    getStatusClass(status) {
      const statusObj = this.statuses.find((s) => s.value === status);
      return statusObj ? statusObj.class : "bg-secondary";
    },

    setCurrentProfile(userId) {
      // Only admins can view other profiles
      if (!this.isAdmin && userId !== firebase.auth().currentUser.uid) {
        return;
      }
      this.currentUserProfile = userId;
      this.fetchJobs(); // Refresh jobs for the new profile
    },

    getJobsForDate(date) {
      // Ensure we get the local date string in YYYY-MM-DD format
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const dateStr = `${year}-${month}-${day}`;

      return Object.entries(this.filteredJobs)
        .filter(([_, job]) => job.scheduledDate === dateStr)
        .map(([id, job]) => ({
          id,
          ...job,
          timeSlot: job.scheduledTime || "",
          status: job.status || "scheduled"
        }));
    },

    checkSlotAvailability(date, timeSlot, duration, excludeJobId = null) {
      // Instead of using new Date().toISOString().split('T')[0]
      // which can cause timezone issues, directly handle the date

      let dateStr;

      // If already in YYYY-MM-DD format, use directly
      if (typeof date === "string" && date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        dateStr = date;
      } else if (date instanceof Date) {
        // If it's a Date object, format it directly using local components
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        dateStr = `${year}-${month}-${day}`;
      } else {
        // For other formats, try to parse carefully
        try {
          // Only non-ISO strings reach here; they parse as local time already.
          const dateObj = new Date(date);
          if (!isNaN(dateObj.getTime())) {
            const year = dateObj.getFullYear();
            const month = String(dateObj.getMonth() + 1).padStart(2, "0");
            const day = String(dateObj.getDate()).padStart(2, "0");
            dateStr = `${year}-${month}-${day}`;
          } else {
            console.error("Invalid date in checkSlotAvailability:", date);
            return true; // Allow scheduling as fallback
          }
        } catch (error) {
          console.error("Error parsing date in checkSlotAvailability:", error);
          return true; // Allow scheduling as fallback
        }
      }

      console.log("Checking availability for date string:", dateStr);

      const dateJobs = Object.entries(this.jobs)
        .filter(([id, job]) => {
          // Skip the current job if we're rescheduling
          if (excludeJobId && id === excludeJobId) return false;
          return job.scheduledDate === dateStr;
        })
        .map(([_, job]) => job);

      // Convert timeSlot to minutes since day start
      const requestedStartMinutes = this.timeToMinutes(timeSlot);
      const requestedEndMinutes = requestedStartMinutes + parseInt(duration);

      // Check for overlaps with existing jobs
      return !dateJobs.some((job) => {
        const jobStartMinutes = this.timeToMinutes(job.scheduledTime);
        const jobEndMinutes = jobStartMinutes + (job.duration || 60);

        // Check if there's any overlap
        return (
          requestedStartMinutes < jobEndMinutes &&
          requestedEndMinutes > jobStartMinutes
        );
      });
    },

    timeToMinutes(time) {
      const [hours, minutes] = time.split(":").map(Number);
      return hours * 60 + minutes;
    },

    getEventStyle(job) {
      const baseStyle = this.getEventPosition(job);
      const statusClass = this.getStatusClass(job.status);
      const backgroundColor = this.getStatusBackgroundColor(job.status);

      return {
        ...baseStyle,
        backgroundColor,
        cursor: "pointer",
        border: "1px solid rgba(0,0,0,0.1)",
        borderRadius: "4px",
        padding: "4px",
        position: "absolute",
        width: "calc(100% - 8px)",
        left: "4px",
        overflow: "hidden"
      };
    },

    getStatusBackgroundColor(status) {
      const statusMap = {
        scheduled: "#007bff",
        completed: "#28a745",
        cancelled: "#dc3545",
        rescheduled: "#ffc107"
      };
      return statusMap[status] || "#6c757d";
    },

    async scheduleJob() {
      if (!this.selectedJob || !this.scheduleForm.timeSlot) return;

      try {
        // Final availability check before scheduling
        const isAvailable = this.checkSlotAvailability(
          this.scheduleForm.date,
          this.scheduleForm.timeSlot,
          this.scheduleForm.duration,
          this.selectedJob.id // Exclude current job when rescheduling
        );

        if (!isAvailable) {
          alert(
            "This time slot is no longer available. Please select another time."
          );
          return;
        }

        await firebase
          .database()
          .ref(`jobs/${this.selectedJob.id}`)
          .update({
            scheduledDate: this.scheduleForm.date,
            scheduledTime: this.scheduleForm.timeSlot,
            schedulingNotes: this.scheduleForm.notes,
            status: "scheduled",
            duration: parseInt(this.scheduleForm.duration),
            scheduledAt: firebase.database.ServerValue.TIMESTAMP
          });

        this.closeModals();
        await this.fetchJobs();
      } catch (error) {
        console.error("Error scheduling job:", error);
      }
    },

    async unscheduleJob(jobId) {
      try {
        await firebase.database().ref(`jobs/${jobId}`).update({
          // Remove scheduling information
          scheduledDate: null,
          scheduledTime: null,
          schedulingNotes: null,
          duration: null,
          scheduledAt: null,
          status: "unscheduled",
          lastUpdated: firebase.database.ServerValue.TIMESTAMP
        });

        // Close modal if the unscheduled job is currently selected
        if (this.selectedJob?.id === jobId) {
          this.closeModals();
        }

        // Refresh jobs data
        await this.fetchJobs();
      } catch (error) {
        console.error("Error unscheduling job:", error);
      }
    },

    async updateJobStatus(jobId, newStatus) {
      try {
        if (newStatus === "unscheduled") {
          await this.unscheduleJob(jobId);
          return;
        }

        await firebase.database().ref(`jobs/${jobId}`).update({
          status: newStatus,
          lastUpdated: firebase.database.ServerValue.TIMESTAMP
        });

        // Update local state
        if (this.selectedJob && this.selectedJob.id === jobId) {
          this.selectedJob = {
            ...this.selectedJob,
            status: newStatus
          };
        }

        // Refresh jobs data
        await this.fetchJobs();
      } catch (error) {
        console.error("Error updating job status:", error);
      }
    },

    unscheduledJobs() {
      if (!this.jobs) return [];
      return Object.entries(this.jobs)
        .filter(([_, job]) => {
          // Include jobs with no schedule or explicitly unscheduled status
          return (
            (!job.scheduledDate && !job.scheduledTime) ||
            job.status === "unscheduled"
          );
        })
        .map(([id, job]) => ({
          id,
          ...job
        }));
    },

    openJobDetails(job) {
      this.selectedJob = job;
      this.showJobDetailsModal = true;
      this.showScheduleModal = false; // Ensure other modal is closed
      this.modalBackdropVisible = true;
    },

    closeModals() {
      this.showScheduleModal = false;
      this.showJobDetailsModal = false;
      this.modalBackdropVisible = false;
      this.selectedJob = null;
      // Reset form
      this.scheduleForm = {
        date: new Date().toISOString().split("T")[0],
        timeSlot: "",
        notes: "",
        status: "scheduled",
        duration: 60
      };
    },

    openScheduleModal(job) {
      this.selectedJob = job;

      // Debug: log the job being scheduled
      console.log("Opening schedule modal for job:", {
        id: job.id,
        originalDate: job.scheduledDate,
        dateType: typeof job.scheduledDate
      });

      // Use today's date if no scheduled date exists
      const today = new Date();
      // Create today's date string directly from components to avoid timezone issues
      const todayStr = `${today.getFullYear()}-${String(
        today.getMonth() + 1
      ).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

      let scheduledDate;

      // Make sure we preserve the original date when rescheduling
      if (job.scheduledDate) {
        // If already in YYYY-MM-DD format, use directly
        if (
          typeof job.scheduledDate === "string" &&
          job.scheduledDate.match(/^\d{4}-\d{2}-\d{2}$/)
        ) {
          scheduledDate = job.scheduledDate;
          console.log(
            "Using existing date in YYYY-MM-DD format:",
            scheduledDate
          );
        } else {
          // Handle other formats
          try {
            if (job.scheduledDate instanceof Date) {
              // Get date components and recreate the date string
              const year = job.scheduledDate.getFullYear();
              const month = String(job.scheduledDate.getMonth() + 1).padStart(
                2,
                "0"
              );
              const day = String(job.scheduledDate.getDate()).padStart(2, "0");
              scheduledDate = `${year}-${month}-${day}`;
              console.log(
                "Created date string from Date object:",
                scheduledDate
              );
            } else if (typeof job.scheduledDate === "string") {
              // Try parsing the string date
              // Date-only ISO needs the noon suffix or it parses as UTC midnight
              // and reads back a day early; any other format must not get it.
              const parsedDate = /^\d{4}-\d{2}-\d{2}$/.test(job.scheduledDate)
                ? new Date(job.scheduledDate + "T12:00:00")
                : new Date(job.scheduledDate);
              if (!isNaN(parsedDate.getTime())) {
                const year = parsedDate.getFullYear();
                const month = String(parsedDate.getMonth() + 1).padStart(
                  2,
                  "0"
                );
                const day = String(parsedDate.getDate()).padStart(2, "0");
                scheduledDate = `${year}-${month}-${day}`;
                console.log(
                  "Parsed string date into YYYY-MM-DD:",
                  scheduledDate
                );
              } else {
                scheduledDate = todayStr;
                console.warn(
                  "Could not parse date string, using today:",
                  scheduledDate
                );
              }
            } else {
              scheduledDate = todayStr;
              console.warn("Unknown date format, using today:", scheduledDate);
            }
          } catch (error) {
            console.error("Error parsing date:", error);
            scheduledDate = todayStr; // Fallback to today
          }
        }
      } else {
        scheduledDate = todayStr; // No date, use today
        console.log("No existing date, using today:", scheduledDate);
      }

      this.scheduleForm = {
        date: scheduledDate,
        timeSlot: job.scheduledTime || "",
        notes: job.schedulingNotes || "",
        status: "scheduled",
        duration: job.duration || 60
      };

      console.log(
        "Schedule form initialized with date:",
        this.scheduleForm.date
      );

      this.showScheduleModal = true;
      this.showJobDetailsModal = false;
    },

    async scheduleJob() {
      if (!this.selectedJob || !this.scheduleForm.timeSlot) {
        console.error("Missing required job or time slot");
        return;
      }

      try {
        // Ensure the date is in the correct format (YYYY-MM-DD)
        let scheduledDate = this.scheduleForm.date;

        // Debug the form input
        console.log("Schedule form submitted with:", {
          date: this.scheduleForm.date,
          timeSlot: this.scheduleForm.timeSlot,
          duration: this.scheduleForm.duration
        });

        // If the date isn't already in YYYY-MM-DD format, convert it
        if (
          typeof scheduledDate !== "string" ||
          !scheduledDate.match(/^\d{4}-\d{2}-\d{2}$/)
        ) {
          try {
            if (scheduledDate instanceof Date) {
              // If it's a Date object, format it directly
              const year = scheduledDate.getFullYear();
              const month = String(scheduledDate.getMonth() + 1).padStart(
                2,
                "0"
              );
              const day = String(scheduledDate.getDate()).padStart(2, "0");
              scheduledDate = `${year}-${month}-${day}`;
            } else {
              // Try to parse as a Date first
              const dateObj = new Date(scheduledDate);
              if (!isNaN(dateObj.getTime())) {
                // Format using local date components
                const year = dateObj.getFullYear();
                const month = String(dateObj.getMonth() + 1).padStart(2, "0");
                const day = String(dateObj.getDate()).padStart(2, "0");
                scheduledDate = `${year}-${month}-${day}`;
              } else {
                throw new Error("Invalid date");
              }
            }
          } catch (error) {
            console.error(
              "Invalid date format in scheduleForm:",
              scheduledDate,
              error
            );
            alert("Invalid date format. Please select a valid date.");
            return;
          }
        }

        console.log("Scheduling job with date:", scheduledDate);

        // Final availability check before scheduling
        const isAvailable = this.checkSlotAvailability(
          scheduledDate,
          this.scheduleForm.timeSlot,
          this.scheduleForm.duration,
          this.selectedJob.id // Exclude current job when rescheduling
        );

        if (!isAvailable) {
          alert(
            "This time slot is no longer available. Please select another time."
          );
          return;
        }

        // Store a reference to the job's date before update for comparison
        const oldDateStr = this.selectedJob.scheduledDate;

        await firebase
          .database()
          .ref(`jobs/${this.selectedJob.id}`)
          .update({
            scheduledDate: scheduledDate, // This is now guaranteed to be YYYY-MM-DD
            scheduledTime: this.scheduleForm.timeSlot,
            schedulingNotes: this.scheduleForm.notes,
            status: "scheduled",
            duration: parseInt(this.scheduleForm.duration),
            scheduledAt: firebase.database.ServerValue.TIMESTAMP
          });

        console.log(
          `Job scheduled: Changed date from ${oldDateStr} to ${scheduledDate}`
        );

        this.closeModals();
        await this.fetchJobs();
      } catch (error) {
        console.error("Error scheduling job:", error);
        alert("An error occurred while scheduling the job. Please try again.");
      }
    },

    // Fixed version of formatDateForDisplay to handle dates consistently
    formatDateForDisplay(date) {
      if (!date) return "";
      try {
        // Handle Date objects directly
        if (date instanceof Date) {
          return date.toLocaleDateString("en-US", {
            weekday: "short",
            month: "short",
            day: "numeric"
          });
        }

        // For string dates in YYYY-MM-DD format, parse components directly
        if (typeof date === "string" && date.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const [year, month, day] = date.split("-").map(Number);
          const localDate = new Date(year, month - 1, day);

          return localDate.toLocaleDateString("en-US", {
            weekday: "short",
            month: "short",
            day: "numeric"
          });
        }

        // For other string formats, ensure proper parsing
        if (typeof date === "string") {
          // Only non-ISO strings reach here; they parse as local time already.
          const dateObj = new Date(date);
          if (isNaN(dateObj.getTime())) {
            console.error("Invalid date in formatDateForDisplay:", date);
            return "";
          }

          return dateObj.toLocaleDateString("en-US", {
            weekday: "short",
            month: "short",
            day: "numeric"
          });
        }

        // Fallback to standard handling with appropriate error logging
        console.warn("Unknown date format in formatDateForDisplay:", date);
        return new Date(date).toLocaleDateString("en-US", {
          weekday: "short",
          month: "short",
          day: "numeric"
        });
      } catch (error) {
        console.error("Invalid date in formatDateForDisplay:", date, error);
        return "";
      }
    },

    // Methods for enhanced calendar navigation
    navigateToWeek(startDate) {
      // Set selectedDate to the start of the selected week
      this.selectedDate = new Date(startDate);
    },

    navigateToMonth(monthIndex) {
      const newDate = new Date(this.selectedDate);
      newDate.setMonth(monthIndex);

      // If the day doesn't exist in the new month (e.g., Jan 31 -> Feb 31),
      // JavaScript will automatically adjust, but we might want to handle this more gracefully
      if (newDate.getMonth() !== monthIndex) {
        // This happens when the day doesn't exist in the target month
        // Set to the last day of the target month
        newDate.setDate(0); // Sets to last day of previous month
      }

      this.selectedDate = newDate;
    },

    isCurrentWeek(weekStartDate) {
      // Check if the given week start date matches the current week
      const currentWeekStart = new Date(this.selectedDate);
      const currentDay = currentWeekStart.getDay(); // 0-6, 0 is Sunday
      currentWeekStart.setDate(currentWeekStart.getDate() - currentDay);

      // Compare just the dates (not time)
      currentWeekStart.setHours(0, 0, 0, 0);
      const compareDate = new Date(weekStartDate);
      compareDate.setHours(0, 0, 0, 0);

      return currentWeekStart.getTime() === compareDate.getTime();
    },

    isSelectedMonth(monthIndex) {
      return this.selectedDate.getMonth() === monthIndex;
    },

    formatWeekRange(startDate, endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);

      const startMonth = start.toLocaleDateString("en-US", { month: "short" });
      const endMonth = end.toLocaleDateString("en-US", { month: "short" });

      const startDay = start.getDate();
      const endDay = end.getDate();

      // If both dates are in the same month
      if (
        start.getMonth() === end.getMonth() &&
        start.getFullYear() === end.getFullYear()
      ) {
        return `${startMonth} ${startDay}-${endDay}`;
      }
      // If different months but same year
      else if (start.getFullYear() === end.getFullYear()) {
        return `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
      }
      // If different years
      else {
        const startYear = start.getFullYear();
        const endYear = end.getFullYear();
        return `${startMonth} ${startDay}, ${startYear} - ${endMonth} ${endDay}, ${endYear}`;
      }
    },

    updateMonthDropdown() {
      // This method is called when the year changes in the dropdown
      // The tempYear computed property with setter handles the actual date update
      // This method can be used for any additional logic needed when year changes
      console.log("Year updated to:", this.tempYear);
    }
  },

  watch: {
    showJobDetailsModal(newValue) {
      this.modalBackdropVisible = newValue;
    },
    showScheduleModal(newValue) {
      this.modalBackdropVisible = newValue;
    },

    currentUserProfile() {
      this.fetchJobs(); // Reload jobs when profile changes
    }
  },

  mounted() {
    this.fetchJobs();
  
    // Check for query parameters to auto-open modal
    if (this.$route.query.jobId && this.$route.query.openModal === 'true') {
      // Wait for jobs to load, then find and open the modal for the specified job
      this.$nextTick(async () => {
        // Give a small delay to ensure jobs are loaded
        setTimeout(() => {
          const jobId = this.$route.query.jobId;
          const job = this.jobs[jobId];

          if (job) {
            console.log('Auto-opening schedule modal for job:', jobId);
            this.openScheduleModal({
              id: jobId,
              ...job
            });

            // Clean up the URL by removing the query parameters
            this.$router.replace({
              name: 'jobScheduler',
              query: {}
            });
          } else {
            console.warn('Job not found for auto-scheduling:', jobId);
          }
        }, 500); // 500ms delay to ensure data is loaded
      });
    }

    // An unconditional listener on the whole jobs node used to live here. It read
    // every job for every user, it duplicated the properly-branched listener set
    // up below once auth resolves, and it is the one thing that would break for a
    // non-admin once the rules stop allowing unrestricted reads.
    firebase.auth().onAuthStateChanged(async (user) => {
      if (user) {
        await this.checkAdminStatus();
        this.currentUserProfile = user.uid; // Default to own profile
        await this.fetchJobs();

        // Set up real-time updates for relevant jobs
        // child_* rather than value. A value listener re-downloads the ENTIRE
        // node every time any single job changes: at 2,000 jobs that is 6 MB per
        // edit, per connected admin. child_added/changed/removed transfer only
        // the job that actually changed. Admin.js already does it this way.
        const jobsRef = firebase.database().ref("jobs");
        const scoped = this.isAdmin
          ? jobsRef
          : jobsRef.orderByChild("userId").equalTo(user.uid);

        this.jobsQuery = scoped;
        this.jobsHandlers = {
          child_added: scoped.on("child_added", (snap) => {
            this.jobs = { ...this.jobs, [snap.key]: snap.val() };
            this.loading = false;
          }),
          child_changed: scoped.on("child_changed", (snap) => {
            this.jobs = { ...this.jobs, [snap.key]: snap.val() };
          }),
          child_removed: scoped.on("child_removed", (snap) => {
            const next = { ...this.jobs };
            delete next[snap.key];
            this.jobs = next;
          }),
        };
        // child_added does not fire for an empty node, so nothing would clear
        // the spinner for a user with no jobs.
        scoped.once("value", () => {
          this.loading = false;
        });
      } else {
        this.jobs = {};
        this.isAdmin = false;
        this.currentUserProfile = null;
      }
    });
  },

  unmounted() {
    // No outer jobsListener guard: that variable no longer exists, and leaving
    // the check in place would silently skip detaching and leak the listeners.
    if (this.jobsQuery && this.jobsHandlers) {
      for (const [event, handler] of Object.entries(this.jobsHandlers)) {
        this.jobsQuery.off(event, handler);
      }
      this.jobsQuery = null;
      this.jobsHandlers = null;
    }
  },

  template: "#job-scheduler"
};
